/**
 * 着荷確認モーダル
 *
 * 設計書 05-frontend-design.md §5.4 整合。
 * 発注情報を読み取り専用表示し、着荷時備考(任意)を受け取って `confirmArrival` API を呼ぶ。
 */
import { useState } from 'react';
import { Modal, Form, Input, Descriptions, message } from 'antd';
import { logger } from '@/shared/lib/logger';
import { confirmArrival } from '../services/purchaseOrderApiService';
import { formatDate } from '@/shared/utils/dateFormatter';
import type {
  ConfirmArrivalRequest,
  ConfirmArrivalResult,
  PurchaseOrderViewModel,
} from '../types/purchaseOrder';

const { TextArea } = Input;

const ARRIVAL_NOTE_TEXTAREA_ROWS = 3;

interface FormValues {
  readonly arrivalNote?: string;
}

interface Props {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onConfirmed: (params: { readonly result: ConfirmArrivalResult }) => void;
  readonly purchaseOrder: PurchaseOrderViewModel;
}

export function ArrivalConfirmationModal({
  open,
  onClose,
  onConfirmed,
  purchaseOrder,
}: Props): React.ReactElement {
  const [form] = Form.useForm<FormValues>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleSubmit = async (values: FormValues): Promise<void> => {
    setIsSubmitting(true);
    try {
      const request: ConfirmArrivalRequest = {
        arrivalNote: values.arrivalNote ?? null,
      };
      const result = await confirmArrival({
        purchaseOrderId: purchaseOrder.id,
        request,
      });
      message.success('入庫確認を実行しました');
      form.resetFields();
      onConfirmed({ result });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : '入庫確認に失敗しました';
      message.error(errorMessage);
      logger.captureError(error, `ArrivalConfirmationModal.handleSubmit(${purchaseOrder.id})`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      open={open}
      title="入庫確認"
      okText="入庫確認を実行"
      cancelText="キャンセル"
      confirmLoading={isSubmitting}
      onOk={() => form.submit()}
      onCancel={handleCancel}
      destroyOnHidden
    >
      <Descriptions size="small" column={1} bordered style={{ marginBottom: 16 }}>
        <Descriptions.Item label="現場">{purchaseOrder.siteName}</Descriptions.Item>
        <Descriptions.Item label="材料">{purchaseOrder.materialTypeName}</Descriptions.Item>
        <Descriptions.Item label="発注数量">{purchaseOrder.orderedQuantity}</Descriptions.Item>
        <Descriptions.Item label="入庫予定日">
          {formatDate(purchaseOrder.expectedArrivalDate)}
        </Descriptions.Item>
        <Descriptions.Item label="発注者">{purchaseOrder.orderedByUserName}</Descriptions.Item>
        {purchaseOrder.orderNote !== null && (
          <Descriptions.Item label="発注備考">{purchaseOrder.orderNote}</Descriptions.Item>
        )}
      </Descriptions>

      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item name="arrivalNote" label="入庫時備考(任意)">
          <TextArea rows={ARRIVAL_NOTE_TEXTAREA_ROWS} placeholder="備考を入力" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
